import React, { useContext, useEffect, useState } from 'react';
import { Spin, Empty, Flex, Typography, Button, Progress, Timeline } from 'antd';
import classNames from 'classnames';
import {
  LeftOutlined,
  RightOutlined,
  PhoneOutlined,
  MessageOutlined,
  SoundOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { CostTypeRef } from '@leedpulse-monorepo/enums';
import { nextCreditsResetAt } from '@leedpulse-monorepo/utils';
import { formatDate, isUnlimitedLeedbucks } from '../../../helpers';
import CompanyContext from '../../../contexts/company/CompanyContext';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import {
  getSubscriptionConsumptionHistory,
  getCycleMovements,
  ConsumptionHistory,
  ConsumptionCycle,
  ConsumptionMovement,
  CycleSummary,
} from '../../../../domain/use_cases/leedflow/getSubscriptionConsumptionHistory';
import { colors } from '../../../../domain/types/colors';

import styles from './SubscriptionConsumptionHistory.module.css';

const { Text, Title } = Typography;

interface SubscriptionConsumptionHistoryProps {
  open: boolean;
  idSubscription: string | null | undefined;
  // Notifies the parent once the history is fetched (e.g. to read payg_rate).
  onHistoryLoaded?: (history: ConsumptionHistory) => void;
}

const formatNumber = (value: number): string => value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
const formatEuro = (value: number): string => value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// A call priced to the thousandth keeps its exact amount; the cycle total stays in cents, as on the invoice
const formatEuroExact = (value: number): string => value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
const formatUnitPrice = (value: number): string => value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
const LINE_TYPE_LABEL: Record<string, string> = { landline: 'Fixe', cellular: 'Mobile', unknown: 'Type indéterminé' };

const MOVEMENT_META: Record<string, { icon: React.ReactNode; label: string }> = {
  [CostTypeRef.CALL]: { icon: <PhoneOutlined />, label: 'Appel' },
  [CostTypeRef.SMS_OUTBOUND]: { icon: <MessageOutlined />, label: 'SMS envoyé' },
  [CostTypeRef.SMS_INBOUND]: { icon: <MessageOutlined />, label: 'SMS reçu' },
  [CostTypeRef.VOICEMAIL]: { icon: <SoundOutlined />, label: 'Messagerie vocale' },
  [CostTypeRef.CALL_RECEPTIONIST]: { icon: <CustomerServiceOutlined />, label: 'Réceptionniste IA' },
};

const getMovementMeta = (typeRef: string | null) =>
  (typeRef && MOVEMENT_META[typeRef]) || { icon: <DollarOutlined />, label: typeRef ?? 'Consommation' };

const getMovementDetail = (movement: ConsumptionMovement): string => {
  const date = movement.created ? formatDate(movement.created) : '';
  const contact = `${movement.contact_firstname ?? ''} ${movement.contact_lastname ?? ''}`.trim();
  return [date, contact].filter(Boolean).join(' · ');
};

// Duration (calls / voicemail) or quantity (SMS), shown on the movement's second line.
const getMovementUnits = (movement: ConsumptionMovement): string => {
  if (movement.units == null) return '';
  if (movement.type_ref === CostTypeRef.CALL || movement.type_ref === CostTypeRef.CALL_RECEPTIONIST || movement.type_ref === CostTypeRef.VOICEMAIL) {
    const totalSeconds = Math.round(movement.units * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return secs > 0 ? `${mins} min ${String(secs).padStart(2, '0')} s` : `${mins} min`;
  }
  return `${formatNumber(movement.units)} SMS`;
};

const SubscriptionConsumptionHistory: React.FC<SubscriptionConsumptionHistoryProps> = ({ open, idSubscription, onHistoryLoaded }) => {
  const { activeCompany } = useContext(CompanyContext);
  const { authentication } = useContext(AuthenticationContext);

  const [history, setHistory] = useState<ConsumptionHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [cycleIndex, setCycleIndex] = useState(0);

  const [summary, setSummary] = useState<CycleSummary | null>(null);
  const [movements, setMovements] = useState<ConsumptionMovement[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [overageRemaining, setOverageRemaining] = useState(0);
  const [movementsLoading, setMovementsLoading] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!open || !idSubscription || !activeCompany?.api_key || !authentication?.token) {
        setHistory(null);
        return;
      }
      setHistoryLoading(true);
      try {
        const result = await getSubscriptionConsumptionHistory(idSubscription, activeCompany.api_key, authentication);
        setHistory(result);
        onHistoryLoaded?.(result);
        // Default to the current cycle
        const currentIdx = result.cycles.findIndex((c) => c.is_current);
        setCycleIndex(currentIdx >= 0 ? currentIdx : Math.max(0, result.cycles.length - 1));
      } catch (e) {
        console.error('SubscriptionConsumptionHistory – error fetching consumption history:', e);
        setHistory(null);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idSubscription, activeCompany?.api_key, authentication]);

  const selectedCycle: ConsumptionCycle | null = history?.cycles?.[cycleIndex] ?? null;
  const cycleStart = selectedCycle?.start ?? null;
  const cycleEnd = selectedCycle?.end ?? null;

  // Cycle summary + movements are loaded lazily, only for the cycle currently displayed.
  useEffect(() => {
    const fetchMovements = async () => {
      if (!open || cycleStart == null || cycleEnd == null || !idSubscription || !activeCompany?.api_key || !authentication?.token) {
        setSummary(null);
        setMovements([]);
        setNextCursor(null);
        setOverageRemaining(0);
        return;
      }
      setMovementsLoading(true);
      try {
        const page = await getCycleMovements(idSubscription, activeCompany.api_key, authentication, { cycleStart, cycleEnd });
        setSummary(page.summary ?? null);
        setMovements(page.movements);
        setNextCursor(page.next_cursor);
        setOverageRemaining(page.overage_remaining);
      } catch (e) {
        console.error('SubscriptionConsumptionHistory – error fetching cycle movements:', e);
        setSummary(null);
        setMovements([]);
        setNextCursor(null);
        setOverageRemaining(0);
      } finally {
        setMovementsLoading(false);
      }
    };
    fetchMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cycleStart, cycleEnd, idSubscription, activeCompany?.api_key, authentication]);

  const loadMoreMovements = async () => {
    if (nextCursor == null || cycleStart == null || cycleEnd == null || !idSubscription || !activeCompany?.api_key || !authentication?.token) return;
    setMovementsLoading(true);
    try {
      const page = await getCycleMovements(idSubscription, activeCompany.api_key, authentication, { cycleStart, cycleEnd, cursor: nextCursor, overageRemaining });
      setMovements((prev) => [...prev, ...page.movements]);
      setNextCursor(page.next_cursor);
      setOverageRemaining(page.overage_remaining);
    } catch (e) {
      console.error('SubscriptionConsumptionHistory – error loading more movements:', e);
    } finally {
      setMovementsLoading(false);
    }
  };

  // Infinite scroll (same behaviour as mobile: loads the next page when approaching the bottom).
  const onTimelineScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (nextCursor != null && !movementsLoading && el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      loadMoreMovements();
    }
  };

  // What the cycle was granted, promotional or not, plus what was added by hand during it, current
  // one included: a promo that ends or starts during the cycle does not change what the cycle received
  const grantedForCycle = summary?.included ?? history?.included;
  const cycleIncluded = (grantedForCycle ?? 0) + (summary?.granted ?? 0);
  const cycleUsedPercent = summary && cycleIncluded > 0
    ? Math.round((summary.consumed / cycleIncluded) * 100)
    : 0;
  const historyUnlimited = isUnlimitedLeedbucks(history?.included ?? 0);

  return (
    <Spin spinning={historyLoading}>
      {!selectedCycle ? (
        <Empty description="Aucune consommation" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Flex vertical gap={14}>
          <Flex align="center" justify="space-between" className={styles.historyNav}>
            <Button
              type="text"
              icon={<LeftOutlined />}
              disabled={cycleIndex <= 0}
              onClick={() => setCycleIndex((i) => Math.max(0, i - 1))}
            />
            <Text strong>
              {formatDate(selectedCycle.start)} - {formatDate(selectedCycle.end)}
            </Text>
            <Button
              type="text"
              icon={<RightOutlined />}
              disabled={!history || cycleIndex >= history.cycles.length - 1}
              onClick={() => setCycleIndex((i) => Math.min((history?.cycles.length ?? 1) - 1, i + 1))}
            />
          </Flex>

          {/* Résumé du cycle */}
          <Spin spinning={movementsLoading && !summary}>
            <Flex vertical gap={8} className={styles.historySummary}>
              <Flex align="center" justify="space-between" gap={12}>
                <span className={styles.historyLabel}>Forfait consommé</span>
                <Flex gap={6} wrap="wrap" justify="flex-end">
                  {/* The stored end is excluded: the promo's last day is the one before */}
                  {selectedCycle.is_current && history?.promo_until && (
                    <span className={styles.cycleChip}>Promotion jusqu'au {formatDate(history.promo_until - 1)}</span>
                  )}
                  <span className={styles.cycleChip}>
                    {selectedCycle.is_current
                      ? `Recharge du forfait · ${formatDate(nextCreditsResetAt(selectedCycle.end), { time: true, until: true })}`
                      : `Cycle terminé le ${formatDate(selectedCycle.end)}`}
                  </span>
                </Flex>
              </Flex>
              {historyUnlimited ? (
                <Flex align="baseline" gap={8}>
                  <Title level={3} className={styles.historyPercent}>{formatNumber(summary?.consumed ?? 0)}</Title>
                  <span className={styles.historyRatio}>crédits consommés</span>
                </Flex>
              ) : (
                <>
                  <Flex align="baseline" gap={8}>
                    <Title level={3} className={classNames(styles.historyPercent, cycleUsedPercent >= 100 && styles.historyPercentAmber)}>{cycleUsedPercent} %</Title>
                    <span className={styles.historyRatio}>({formatNumber(summary?.consumed ?? 0)} / {formatNumber(cycleIncluded)} crédits)</span>
                  </Flex>
                  <Progress
                    percent={Math.min(100, Math.max(0, cycleUsedPercent))}
                    showInfo={false}
                    strokeColor={cycleUsedPercent >= 100 ? colors.planCredits.amber : colors.planCredits.green}
                    style={{ margin: 0 }}
                  />
                  {(summary?.overage_amount ?? 0) > 0 && (
                    <span className={styles.historyOverage}>{Math.abs(100 - cycleUsedPercent)} % hors forfait · {formatEuro(summary!.overage_amount)} € en cours</span>
                  )}
                </>
              )}
              {(summary?.destination?.amount ?? 0) > 0 && (
                <span className={styles.historyOverage}>
                  Appels sortants hors forfait · {summary!.destination!.calls} appel{summary!.destination!.calls > 1 ? 's' : ''} · {formatNumber(summary!.destination!.billable_minutes)} min · {formatEuro(summary!.destination!.amount)} €
                </span>
              )}
            </Flex>
          </Spin>

          {/* Mouvements — AntD Timeline + scroll infini (comportement mobile) */}
          <div className={styles.timeline} onScroll={onTimelineScroll}>
            {movements.length === 0 ? (
              movementsLoading ? (
                <div className={styles.timelineLoader}><Spin /></div>
              ) : (
                <Empty description="Aucun mouvement" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            ) : (
              <>
                <Timeline
                  items={movements.map((movement) => {
                    // A recharge during the plan's promotional period is a renewal too
                    if (movement.type_ref === CostTypeRef.CREDITS_RENEWAL || movement.type_ref === CostTypeRef.PROMOTIONAL_CREDITS_RENEWAL) {
                      return {
                        dot: (
                          <Flex align="center" justify="center" className={styles.timelineIcon}>
                            <PlusCircleOutlined />
                          </Flex>
                        ),
                        children: (
                          <div className={styles.movementRow}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={styles.movementTitle}>{movement.type_ref === CostTypeRef.PROMOTIONAL_CREDITS_RENEWAL ? 'Recharge promotionnelle' : 'Recharge du forfait'}</div>
                              <div className={styles.movementSub}>{movement.created ? formatDate(movement.created) : ''}</div>
                            </div>
                            <div className={styles.movementValue}>
                              <span className={styles.movementValueMain} style={{ color: colors.planCredits.green }}>Rechargé</span>
                              {/* The promo replaces the plan's allocation, so its amount is worth showing */}
                              {movement.type_ref === CostTypeRef.PROMOTIONAL_CREDITS_RENEWAL && (
                                <span className={styles.movementUnits}>+ {formatNumber(-movement.credit_consumed)} crédits</span>
                              )}
                            </div>
                          </div>
                        ),
                      };
                    }

                    // Manual adjustment from the back-office: « Geste commercial · +50 crédits »
                    if (movement.type_ref === CostTypeRef.CREDITS_ADJUSTMENT) {
                      const delta = -movement.credit_consumed;
                      return {
                        dot: (
                          <Flex align="center" justify="center" className={styles.timelineIcon}>
                            {delta > 0 ? <PlusCircleOutlined /> : <MinusCircleOutlined />}
                          </Flex>
                        ),
                        children: (
                          <div className={styles.movementRow}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={styles.movementTitle}>{movement.reason_label ?? 'Ajustement'}</div>
                              <div className={styles.movementSub}>{movement.created ? formatDate(movement.created) : ''}</div>
                            </div>
                            <span className={styles.movementValueMain} style={delta > 0 ? { color: colors.planCredits.green } : undefined}>
                              {delta > 0 ? '+' : '-'} {formatNumber(Math.abs(delta))} crédits
                            </span>
                          </div>
                        ),
                      };
                    }

                    const meta = getMovementMeta(movement.type_ref);
                    const units = getMovementUnits(movement);
                    // A destination-priced call is money, shown in the overage colour with no credits
                    const destination = movement.billing ?? null;
                    const overPlan = movement.over_plan || !!destination;
                    return {
                      dot: (
                        <Flex align="center" justify="center" className={classNames(styles.timelineIcon, overPlan && styles.timelineIconOverage)}>
                          {meta.icon}
                        </Flex>
                      ),
                      children: (
                        <div className={styles.movementRow}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={classNames(styles.movementTitle, overPlan && styles.movementTitleOverage)}>
                              {destination
                                ? `Appel sortant hors forfait · ${destination.country_label || destination.country} · ${LINE_TYPE_LABEL[destination.line_type] ?? destination.line_type}`
                                : movement.over_plan
                                  ? `${movement.type_ref === CostTypeRef.SMS_OUTBOUND ? 'SMS' : meta.label} hors forfait`
                                  : meta.label}
                            </div>
                            <div className={styles.movementSub}>{getMovementDetail(movement)}</div>
                          </div>
                          <div className={styles.movementValue}>
                            <span className={classNames(styles.movementValueMain, overPlan && styles.movementValueMainOverage)}>
                              {destination ? `${formatEuroExact(destination.amount)} €` : movement.credit_consumed === 0 ? 'Inclus' : `- ${formatNumber(movement.credit_consumed)} crédits`}
                            </span>
                            {destination ? (
                              <span className={classNames(styles.movementUnits, styles.movementUnitsOverage)}>
                                ({units ? `${units} · ` : ''}{destination.units} min × {formatUnitPrice(destination.unit_price)} €/min · 0 crédit)
                              </span>
                            ) : units && (
                              <span className={classNames(styles.movementUnits, movement.over_plan && styles.movementUnitsOverage)}>
                                ({units}{movement.over_plan && movement.overage_amount > 0 ? ` · ${formatEuro(movement.overage_amount)} €` : ''})
                              </span>
                            )}
                          </div>
                        </div>
                      ),
                    };
                  })}
                />
                {movementsLoading && <div className={styles.timelineLoader}><Spin size="small" /></div>}
              </>
            )}
          </div>
        </Flex>
      )}
    </Spin>
  );
};

export default SubscriptionConsumptionHistory;
